import React, { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { useSiteOfficeSelection } from '@/hooks/useSiteOfficeSelection';
import SiteOfficeHeader from './site-office/SiteOfficeHeader';
import SiteOfficeGrid from './site-office/SiteOfficeGrid';
import SiteOfficeActions from './site-office/SiteOfficeActions';
import SiteOfficeLoadingState from './site-office/SiteOfficeLoadingState';
import SiteOfficeBackground from './site-office/SiteOfficeBackground';
import SiteOfficeFooter from './site-office/SiteOfficeFooter';
import SiteOfficeProjectOverview from './site-office/SiteOfficeProjectOverview';
import { SiteOffice } from '@/services/siteOfficeService';
import { useSiteOffice } from '@/contexts/SiteOfficeContext';

interface SiteOfficeSelectionProps {
  onSiteOfficeSelected: (siteOffice: string) => void;
}

const SiteOfficeSelection = ({ onSiteOfficeSelected }: SiteOfficeSelectionProps) => {
  const [selectedSiteOfficeForOverview, setSelectedSiteOfficeForOverview] = useState<SiteOffice | null>(null);

  const {
    siteOffices,
    loading,
    selectedOffice,
    customOffice,
    showCustomInput,
    isCreatingCustom,
    isValid,
    handleOfficeSelect,
    handleCustomSelect,
    handleCustomOfficeChange,
    handleEditCustomOffice,
    handleDeleteCustomOffice,
    handleProceed
  } = useSiteOfficeSelection(onSiteOfficeSelected);

  console.log('Validation state:', { selectedOffice, customOffice, isValid, showCustomInput });

  // Handler for showing project overview
  const handleShowProjectOverview = (officeName: string) => {
    const office = siteOffices.find(o => o.name === officeName);
    if (office) {
      setSelectedSiteOfficeForOverview(office);
    }
  };

  // Enhanced office select handler that also handles selection for form
  const handleEnhancedOfficeSelect = (officeName: string) => {
    handleOfficeSelect(officeName);
    handleShowProjectOverview(officeName);
  };

  if (loading) {
    return <SiteOfficeLoadingState />;
  }

  // Handler for entering site office (callback untuk navigasi)
  const handleEnterSiteOffice = () => {
    if (selectedSiteOfficeForOverview) {
      onSiteOfficeSelected(selectedSiteOfficeForOverview.name);
    }
  };

  // If project overview is selected, show full page view
  if (selectedSiteOfficeForOverview) {
    return (
      <div
        className="relative min-h-screen min-h-svh bg-[#f8fafc] py-4 sm:py-6 lg:py-8"
        style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}
      >
        <div className="page-content-inner">
          <SiteOfficeProjectOverview 
            siteOffice={selectedSiteOfficeForOverview}
            onClose={() => setSelectedSiteOfficeForOverview(null)}
            onEnterSiteOffice={handleEnterSiteOffice}
            isFullPage={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen min-h-svh bg-[#f8fafc] py-4 sm:py-6 lg:py-8"
      style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}
    >
      <SiteOfficeBackground />

      {/* Main Content — same centering as app pages (page-content-inner) */}
      <div className="relative z-10 page-content-inner">
        <div className="flex justify-center">
          <div className="w-full max-w-4xl animate-fadeInUp">
            <Card className="shadow-xl shadow-slate-200/60 border border-slate-100 bg-white overflow-hidden rounded-2xl">
        <SiteOfficeHeader />
        
              <CardContent className="px-6 sm:px-8 lg:px-12 pb-8 sm:pb-10 lg:pb-12">
                <div className="space-y-8 sm:space-y-10">
          <SiteOfficeGrid
            siteOffices={siteOffices}
            selectedOffice={selectedOffice}
            customOffice={customOffice}
            showCustomInput={showCustomInput}
            isCreatingCustom={isCreatingCustom}
                    onOfficeSelect={handleEnhancedOfficeSelect}
            onCustomSelect={handleCustomSelect}
                    onCustomOfficeChange={handleCustomOfficeChange}
                    onEditCustomOffice={handleEditCustomOffice}
                    onDeleteCustomOffice={handleDeleteCustomOffice}
          />
          
          <SiteOfficeActions
            isValid={isValid}
            isCreatingCustom={isCreatingCustom}
            onProceed={handleProceed}
                    siteOffices={siteOffices}
          />
                </div>
        </CardContent>
      </Card>
            
            <SiteOfficeFooter />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteOfficeSelection;
